import type { ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    expandAction,
    type DataTableButtonAction,
    type DataTableExpansion,
    type DataTableFilter,
    type DataTableMode,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSearchConfig,
    type DataTableSort
} from '@odo/shared-ui';
import { heightClassPx } from '../../../../hooks/useViewportPageSize';
import type { Communication } from '../../../../services/communications';
import { buildCommunicationColumns, type CommunicationsSortBy } from './communicationColumns';

const SKELETON_ROW_COUNT = 8;
// Matches the real rows' height so the loading state measures the same as
// the data it stands in for (see useViewportPageSize).
const ROW_HEIGHT = 'h-24';
export const COMMUNICATION_ROW_HEIGHT = heightClassPx(ROW_HEIGHT);

function SkeletonCell({ width, circle }: { width: string; circle?: boolean }) {
    return (
        <div
            className={`bg-gray-200 animate-pulse ${circle ? 'w-7 h-7 rounded-full' : `h-3.5 rounded ${width}`}`}
        />
    );
}

// Matches communicationColumns.tsx's order: Reference, [Practice], Title,
// Categories, Assignee (avatar), Entries, Last activity, Status, then the
// blank menu cell.
function SkeletonRows({ showPractice }: { showPractice: boolean }) {
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className={`${ROW_HEIGHT} border-b border-gray-200`}>
                    <td className="px-3 first:pl-4 py-4">
                        <SkeletonCell width="w-20" />
                    </td>
                    {showPractice && (
                        <td className="px-3 py-4">
                            <SkeletonCell width="w-28" />
                        </td>
                    )}
                    <td className="px-3 py-4">
                        <div className="flex flex-col gap-1.5">
                            <SkeletonCell width="w-48" />
                            <SkeletonCell width="w-32" />
                        </div>
                    </td>
                    <td className="px-3 py-4">
                        <SkeletonCell width="w-20" />
                    </td>
                    <td className="px-3 py-4">
                        <SkeletonCell width="" circle />
                    </td>
                    <td className="px-3 py-4">
                        {/* Interactions - wider than a single-value cell's
                            bar, standing in for a row of icon+count chips. */}
                        <SkeletonCell width="w-24" />
                    </td>
                    <td className="px-3 py-4">
                        <SkeletonCell width="w-16" />
                    </td>
                    <td className="px-3 py-4">
                        <SkeletonCell width="w-16" />
                    </td>
                    <td className="px-3 last:pr-3 py-4" />
                </tr>
            ))}
        </>
    );
}

function CommunicationsErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="py-16 flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-red-500 font-medium">Failed to load communications</p>
            <button
                type="button"
                onClick={onRetry}
                className="text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 transition-colors"
            >
                Retry
            </button>
        </div>
    );
}

interface CommunicationsEmptyStateProps {
    hasSearch: boolean;
    hasFilters: boolean;
    search: string;
    onClearFilters: () => void;
    onLogCommunication?: () => void;
}

// A filtered-empty result reads as the plain "no match" state below, not the
// big onboarding empty state - that one is reserved for a practice with zero
// communications logged at all.
function CommunicationsEmptyState({
    hasSearch,
    hasFilters,
    search,
    onClearFilters,
    onLogCommunication
}: CommunicationsEmptyStateProps) {
    const isFiltered = hasSearch || hasFilters;
    return (
        <div className="py-20 flex flex-col items-center gap-3 text-center">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            {isFiltered ? (
                <>
                    <p className="text-sm text-gray-500">
                        {hasSearch ? (
                            <>No communications match &ldquo;{search}&rdquo;.</>
                        ) : (
                            'No communications match your filters.'
                        )}
                    </p>
                    <button
                        type="button"
                        onClick={onClearFilters}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm mt-1"
                    >
                        {hasFilters ? 'Clear filters' : 'Clear search'}
                    </button>
                </>
            ) : (
                <>
                    <p className="text-sm font-semibold text-gray-700">
                        No communications logged yet
                    </p>
                    <p className="text-[13px] text-gray-500 max-w-[380px]">
                        {onLogCommunication
                            ? 'When this practice reaches out, log it here - a reference number is generated for you to read back to them.'
                            : 'Communications are logged from a practice, and every one that has been logged shows up here.'}
                    </p>
                    {onLogCommunication && (
                        <button
                            type="button"
                            onClick={onLogCommunication}
                            className="flex items-center gap-2 bg-gray-900 text-white font-semibold px-4 h-9 rounded-xl text-sm hover:bg-black transition-colors mt-1"
                        >
                            <ChatBubbleLeftRightIcon className="h-4 w-4" />
                            Log a communication
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

interface CommunicationsTableProps {
    communications: Communication[];
    isLoading: boolean;
    isError: boolean;
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    onRetry: () => void;
    onLogCommunication?: () => void;
    /** Adds a Practice column - cross-practice lists only. */
    showPractice?: boolean;
    onOpenCommunication: (communication: Communication) => void;
    onDelete: (communication: Communication) => void;
    /** Mode toggle in the footer, and which of the two loaders below feeds the table. */
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    sort: DataTableSort<CommunicationsSortBy>;
    /** Always passed - 'infinite' mode only reads `total` from it. */
    pagination: DataTablePagination;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    tableCardRef: RefObject<HTMLDivElement>;
    tableHeaderRef: RefObject<HTMLTableSectionElement>;
    tableBodyRef: RefObject<HTMLTableSectionElement>;
    paginationRef: RefObject<HTMLDivElement>;
    expansion?: DataTableExpansion;
    /** The live search box - `search` above is the committed value used only for empty-state copy. */
    searchConfig: DataTableSearchConfig;
    /** Filter pills sharing the shared `FilterDropdown` chrome - status, practice, assignee, … */
    filters: readonly DataTableFilter[];
    /** Escape hatch for toolbar content the shared filter pill can't express. */
    toolbarSlot?: ReactNode;
    /** "Log a communication", ahead of the expand toggle - never the expand entry itself. */
    toolbarActions?: readonly DataTableButtonAction[];
}

export function CommunicationsTable({
    communications,
    isLoading,
    isError,
    search,
    hasFilters,
    onClearFilters,
    onRetry,
    onLogCommunication,
    showPractice = false,
    onOpenCommunication,
    onDelete,
    mode,
    onModeChange,
    sort,
    pagination,
    onLoadMore,
    hasMore,
    isLoadingMore,
    tableCardRef,
    tableHeaderRef,
    tableBodyRef,
    paginationRef,
    expansion,
    searchConfig,
    filters,
    toolbarSlot,
    toolbarActions
}: CommunicationsTableProps) {
    // An error always takes over the body, even over stale cached rows.
    const rows = isError ? [] : communications;

    const columns = useMemo(
        () => buildCommunicationColumns({ communications, showPractice, onDelete }),
        [communications, showPractice, onDelete]
    );

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <DataTable
            label="Communications"
            columns={columns}
            rows={rows}
            getRowKey={(communication) => communication.id}
            rowAction={{ onActivate: onOpenCommunication }}
            rowClassName={() => 'group'}
            rowHeightClassName={ROW_HEIGHT}
            sort={sort}
            mode={mode}
            onModeChange={onModeChange}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            loadingRows={<SkeletonRows showPractice={showPractice} />}
            emptyState={
                isError ? (
                    <CommunicationsErrorState onRetry={onRetry} />
                ) : (
                    <CommunicationsEmptyState
                        hasSearch={search.length > 0}
                        hasFilters={hasFilters}
                        search={search}
                        onClearFilters={onClearFilters}
                        onLogCommunication={onLogCommunication}
                    />
                )
            }
            emptyStateCellClassName="px-3"
            pagination={pagination}
            refs={refs}
            className="pb-8 overflow-hidden"
            search={searchConfig}
            filters={filters}
            toolbar={toolbarSlot}
            // "Log a communication" (if any), then the expand toggle - array
            // order is render order, so the toggle always lands last/far right.
            actions={[
                ...(toolbarActions ?? []),
                ...(expansion ? [expandAction(expansion, { label: 'Communications' })] : [])
            ]}
            expansion={expansion}
        />
    );
}
